import { t, translateMessage } from "../i18n";
import { Check, Copy, ExternalLink, KeyRound, ShieldCheck } from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";
import type { PendingMfa } from "../useEasyOrders";

type Props = {
  challenge: PendingMfa | null;
  recoveryCodes: string[];
  busy: boolean;
  message: string;
  onComplete: (code: string) => Promise<void>;
  onCancel: () => void;
  onAcknowledgeRecoveryCodes: () => void;
};

async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

export function TwoFactorScreen({
  challenge,
  recoveryCodes,
  busy,
  message,
  onComplete,
  onCancel,
  onAcknowledgeRecoveryCodes
}: Props) {
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState("");
  const enrolling = challenge?.auth_state === "mfa_enrollment_required";
  const groupedKey = useMemo(
    () => challenge?.manual_key?.match(/.{1,4}/g)?.join(" ") ?? "",
    [challenge?.manual_key]
  );

  if (recoveryCodes.length) {
    const allCodes = recoveryCodes.join("\n");
    return (
      <main className="login-screen mfa-screen">
        <section className="login-panel mfa-panel" aria-labelledby="recovery-title">
          <span className="mfa-hero-icon"><KeyRound /></span>
          <div>
            <h1 id="recovery-title">{t("Κωδικοί ανάκτησης")}</h1>
            <p className="login-subtitle">{t("Φύλαξέ τους σε ασφαλές μέρος. Κάθε κωδικός χρησιμοποιείται μία φορά.")}</p>
          </div>
          <div className="recovery-code-grid" aria-label={t("Κωδικοί ανάκτησης")}>
            {recoveryCodes.map((recoveryCode) => <code key={recoveryCode}>{recoveryCode}</code>)}
          </div>
          <button
            type="button"
            className="secondary-command"
            onClick={async () => {
              if (await copyText(allCodes)) setCopied("recovery");
            }}
          >
            {copied === "recovery" ? <Check /> : <Copy />}
            <span>{copied === "recovery" ? t("Αντιγράφηκαν") : t("Αντιγραφή κωδικών")}</span>
          </button>
          <button
            type="button"
            className="primary-command login-command"
            onClick={onAcknowledgeRecoveryCodes}
          >
            <ShieldCheck />
            <span>{t("Τους αποθήκευσα")}</span>
          </button>
        </section>
      </main>
    );
  }

  if (!challenge) {
    return null;
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!code.trim() || busy) return;
    try {
      await onComplete(code.trim());
    } catch {
      // The parent renders the server message and leaves this challenge open for another try.
    }
  }

  return (
    <main className="login-screen mfa-screen">
      <section className="login-panel mfa-panel" aria-labelledby="mfa-title">
        <span className="mfa-hero-icon"><ShieldCheck /></span>
        <div>
          <h1 id="mfa-title">{enrolling ? t("Ρύθμιση 2FA") : t("Επαλήθευση 2FA")}</h1>
          <p className="login-subtitle">
            {enrolling
              ? t("Σύνδεσε τον λογαριασμό με εφαρμογή authenticator πριν συνεχίσεις.")
              : t("Άνοιξε την εφαρμογή authenticator και γράψε τον τρέχοντα κωδικό.")}
          </p>
        </div>

        {enrolling ? (
          <div className="mfa-enrollment">
            {challenge.qr_png ? (
              <img className="mfa-qr" src={challenge.qr_png} alt={t("QR ρύθμισης 2FA")} />
            ) : null}
            {challenge.otpauth_uri ? (
              <a className="secondary-command mfa-open-authenticator" href={challenge.otpauth_uri}>
                <ExternalLink />
                <span>{t("Άνοιγμα authenticator")}</span>
              </a>
            ) : null}
            <div className="mfa-manual-key">
              <span>{t("Χειροκίνητο κλειδί")}</span>
              <div>
                <code>{groupedKey}</code>
                <button
                  type="button"
                  className="icon-button"
                  aria-label={t("Αντιγραφή κλειδιού")}
                  title={t("Αντιγραφή κλειδιού")}
                  onClick={async () => {
                    if (challenge.manual_key && await copyText(challenge.manual_key)) {
                      setCopied("key");
                    }
                  }}
                >
                  {copied === "key" ? <Check /> : <Copy />}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <form className="login-form mfa-form" onSubmit={submit}>
          <label>
            <span>{enrolling ? t("6ψήφιος κωδικός") : t("Κωδικός 2FA ή ανάκτησης")}</span>
            <input
              autoFocus
              autoComplete="one-time-code"
              inputMode={enrolling ? "numeric" : "text"}
              maxLength={enrolling ? 6 : 14}
              value={code}
              onChange={(event) => setCode(
                enrolling
                  ? event.target.value.replace(/\D/g, "").slice(0, 6)
                  : event.target.value.toUpperCase()
              )}
              required
            />
          </label>
          {message ? <div className="form-message" role="alert">{translateMessage(message)}</div> : null}
          <div className="button-pair mfa-actions">
            <button type="button" className="secondary-command" onClick={onCancel} disabled={busy}>{t("Ακύρωση")}</button>
            <button type="submit" className="primary-command" disabled={busy || !code.trim()}>
              <ShieldCheck />
              <span>{busy ? t("Έλεγχος...") : enrolling ? t("Ενεργοποίηση") : t("Επαλήθευση")}</span>
            </button>
          </div>
        </form>
      </section>
    </main>
  );
}
